// V17.299: AR Commercial daily team board — 55" wall display.
//
// Three things this file exists to get right:
//
// 1. Pairing. The TV has no keyboard, and a normal session dies after 30
//    minutes with no refresh flow. A `?pair=` token is exchanged into
//    localStorage on first load and stripped from the URL, so the credential
//    reaches the access logs once rather than on every 30s poll.
// 2. Honest failure. A board that shows a frozen number behind a green LIVE
//    dot is worse than a blank screen, so a stale payload downgrades the
//    indicator and a rejected token replaces the board outright.
// 3. Paging. The roster is 13-25 officers, more than fits at wall-legible
//    type, so the table cycles while the strip and totals stay put.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import TeamBoardTable from '../../components/ar/TeamBoardTable';
import TeamBoardThemeToggle from '../../components/ar/TeamBoardThemeToggle';
import UninstrumentedBanner from '../../components/ar/UninstrumentedBanner';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAppDate } from '../../hooks/useAppDate';
import { useTheme } from '../../contexts/ThemeContext';
import {
  fetchCommercialTeamBoard,
  fetchCommercialTeamBoardForDisplay,
} from '../../services/arCommercialTeamBoard';
import type { TeamBoard, TeamBoardRow } from '../../services/arTeamBoard';
import { formatLak, isStale } from '../../utils/teamBoardFormat';
import { useTeamBoardStrings } from '../../utils/teamBoardI18n';
import {
  claimPairingToken,
  PAGE_ROTATE_MS,
  paginate,
  POLL_INTERVAL_MS,
  secondsToRefresh,
  topThree,
} from '../../utils/teamBoardTv';

export const DISPLAY_TOKEN_KEY = 'ar_commercial_team_board_display_token';

export default function ARCommercialTeamBoardTV() {
  const [board, setBoard] = useState<TeamBoard | null>(null);
  const [disconnected, setDisconnected] = useState(false);
  const [now, setNow] = useState(new Date());
  // V17.269: when the next poll is due. Stamped as each fetch is dispatched
  // rather than when it resolves — the poll runs on a fixed 30s interval that
  // does not wait for the previous response, so tying the countdown to
  // completion would drift a little further behind on every slow request.
  const [nextRefreshAt, setNextRefreshAt] = useState<number | null>(null);
  const [page, setPage] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();
  // V17.262 Blocker 1 follow-up: this was a `useRef` set during render, guarded
  // by a `=== null` check that only survived because StrictMode's double
  // render happened to leave the ref already populated on the second pass.
  // Now that the wrapper route lets an unauthenticated TV actually reach this
  // component, this is the only place the pairing token gets claimed —
  // making it load-bearing, not incidental. `useState(() => ...)` is the
  // sanctioned run-once form: the initializer runs exactly once, on mount,
  // regardless of StrictMode or re-renders.
  const [token] = useState(() => claimPairingToken(searchParams, DISPLAY_TOKEN_KEY));

  // V17.266: the URL is how a keyboard-less TV is configured, so it — not the
  // persisted vtw_theme value — decides the theme on every mount. Applied via
  // setTheme rather than by touching the class directly: a wrapper `.dark` div
  // cannot force light OFF (Tailwind matches a .dark ANCESTOR), and a direct
  // class effect here would run before ThemeProvider's own effect and be
  // overwritten by it.
  //
  // The in-app "TV Mode" button is an in-tab `navigate('?tv=1')`, so mounting
  // this component overwrites the user's persisted vtw_theme — and pressing
  // Back would otherwise leave them stuck in a theme they never chose. A
  // paired wall TV never unmounts, so it is unaffected; only the in-app round
  // trip needs the restore. `themeOnMount` is a `useRef` (not state) so it is
  // captured exactly once, at mount, and never refreshed by later renders.
  const { theme, setTheme } = useTheme();
  const themeOnMount = useRef(theme);
  const themeParam = searchParams.get('theme')?.toLowerCase();
  useEffect(() => {
    setTheme(themeParam === 'dark' ? 'dark' : 'light');
    return () => setTheme(themeOnMount.current);
    // `theme` deliberately excluded: adding it would re-fire this effect on
    // every theme change and fight its own cleanup. No disable directive is
    // needed — the effect reads the mount-time theme through `themeOnMount`,
    // a ref, so exhaustive-deps never asks for `theme` in the first place.
  }, [themeParam, setTheme]);

  // V17.266: only act when the param is explicitly present. setLanguage PUTs
  // /users/me/language whenever a token exists, so calling it unconditionally
  // would silently rewrite the saved account language of any authenticated
  // person who opens this URL in their own browser. A real TV is
  // unauthenticated with empty storage and already defaults to Lao.
  const { setLanguage } = useLanguage();
  const langParam = searchParams.get('lang')?.toLowerCase();
  useEffect(() => {
    if (langParam === 'en' || langParam === 'lo') void setLanguage(langParam);
  }, [langParam, setLanguage]);

  const t = useTeamBoardStrings();
  const { fmt } = useAppDate();

  // V17.266: Escape exits back to the in-app page — but ONLY when a session
  // is present. This component also renders for an unauthenticated paired
  // wall TV: that display has no keyboard to recover with, and the in-app
  // destination sits behind `<ProtectedRoute>`, which would bounce an
  // unauthenticated visitor straight to `/login` — stranding the board until
  // a leader re-pairs it. Gating on `isAuthenticated` means a stray Escape
  // from a cleaner, a TV remote, or a wireless presenter is a no-op on the
  // wall display, while a human viewing this in-app (via the "TV Mode"
  // button) can still back out.
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  useEffect(() => {
    if (!isAuthenticated) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') navigate('/ar/commercial/team-board');
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    // Strip `?pair=` from the address bar once claimed, so the credential
    // reaches the access logs a single time rather than on every reload.
    if (searchParams.has('pair')) {
      const next = new URLSearchParams(searchParams);
      next.delete('pair');
      setSearchParams(next, { replace: true });
    }
    // Run once on mount only — re-running on every searchParams change
    // would fight the very update this effect performs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = useCallback(async () => {
    setNextRefreshAt(Date.now() + POLL_INTERVAL_MS);
    try {
      const data = token
        ? await fetchCommercialTeamBoardForDisplay(token)
        : await fetchCommercialTeamBoard();
      setBoard(data);
      setDisconnected(false);
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      // Only an auth failure kills the board. A transient 5xx keeps the last
      // good payload, which the stale indicator will flag soon enough.
      if (status === 401 || status === 403) setDisconnected(true);
    }
  }, [token]);

  useEffect(() => {
    void load();
    const poll = setInterval(() => void load(), POLL_INTERVAL_MS);
    const clock = setInterval(() => setNow(new Date()), 1_000);
    return () => {
      clearInterval(poll);
      clearInterval(clock);
    };
  }, [load]);

  const pages = useMemo(() => (board ? paginate(board) : []), [board]);

  useEffect(() => {
    if (pages.length <= 1) return undefined;
    const rotate = setInterval(
      () => setPage((p) => (p + 1) % pages.length),
      PAGE_ROTATE_MS,
    );
    return () => clearInterval(rotate);
  }, [pages.length]);

  if (disconnected) {
    return (
      <div data-testid="display-disconnected"
           className="flex h-screen w-screen flex-col items-center justify-center
                      bg-red-50 p-12 text-center text-red-900
                      dark:bg-red-950 dark:text-red-100">
        <div className="text-6xl font-bold">{t.disconnected}</div>
        <div className="mt-6 text-3xl">{t.commercialRePair}</div>
      </div>
    );
  }

  if (!board) {
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-white text-2xl
                      text-gray-500 dark:bg-slate-950 dark:text-slate-400">
        {t.loading}
      </div>
    );
  }

  const stale = isStale(board.generated_at, now);
  const shown = pages[Math.min(page, pages.length - 1)] ?? board;
  const secondsUntilRefresh = secondsToRefresh(nextRefreshAt, now);

  return (
    <div className="flex h-screen w-screen flex-col bg-white p-8 text-gray-900
                    dark:bg-slate-950 dark:text-slate-100">
      <header className="flex items-start justify-between border-b-2 border-gray-300 pb-3 dark:border-slate-800">
        <div>
          <h1 className="text-4xl font-bold">{t.commercialTitle}</h1>
          <p className="mt-1 text-lg text-gray-500 dark:text-slate-400">
            {board.as_of} · {t.commercialSubtitle}
          </p>
        </div>
        {/* V17.276: margins, not flex `gap` — the Chrome 69 wall TVs ignore
            gap on flex containers (it needs Chrome 84), which crammed these
            blocks together. Grid `gap` (leaders strip below) is fine there. */}
        <div className="flex items-center">
          <div data-testid="live-indicator" className="text-right text-sm">
            <span className={`font-bold ${stale
              ? 'text-amber-700 dark:text-amber-400'
              : 'text-emerald-700 dark:text-emerald-400'}`}>
              {stale ? t.stale : t.live}
            </span>
            <div className="text-gray-500 dark:text-slate-400">
              {t.updated(fmt(board.generated_at, 'time'))}
            </div>
            {/* V17.359: freshness of the Legacy billing pull behind the cash
                columns — `updated` above only says when the payload was
                built, not how old the Legacy data inside it is. */}
            {board.billing_last_synced_at && (
              <div data-testid="legacy-sync-stamp"
                   className="text-gray-500 dark:text-slate-400">
                {t.legacySynced(
                  fmt(board.billing_last_synced_at, 'time'),
                )}
              </div>
            )}
            {/* V17.272: double the surrounding `text-sm` (0.875rem), bold. The
                literal 2x lands between Tailwind's text-2xl and text-3xl, so
                it is spelled as an arbitrary value rather than rounded to
                whichever step happens to be nearer. `leading-tight` keeps the
                taller line from pushing the block past the clock's height. */}
            {/* V17.273: darkened for contrast. On the dark TV theme "darker"
                would mean *less* contrast, so the dark variant goes the other
                way — both sides move away from the background, not toward a
                literally darker hex. Sits one step under the title
                (gray-900 / slate-100) so the countdown reads as prominent
                without competing with the board's own heading. */}
            <div data-testid="refresh-countdown"
                 className="text-[1.75rem] font-bold leading-tight tabular-nums
                            text-gray-700 dark:text-slate-300">
              {t.nextRefresh(secondsUntilRefresh)}
            </div>
          </div>
          <div className="ml-6 text-4xl font-bold tabular-nums">
            {fmt(now, 'time')}
          </div>
          {/* V17.348: shared with the residential wall display. */}
          <TeamBoardThemeToggle />
        </div>
      </header>

      {/* V17.299: a podium of three zeros is worse than no podium — it
          invents a ranking out of an absence of data. `?? true` so a payload
          without `sources` still shows the strip. */}
      {(board.sources?.collections_recorded_90d ?? true) && (
        <div data-testid="leaders-strip"
             className="my-3 grid grid-cols-[150px_repeat(3,1fr)] items-center gap-4
                        rounded-xl border-2 border-gray-200 bg-gray-50 px-5 py-3
                        dark:border-slate-800 dark:bg-slate-900">
          <div className="text-sm font-bold uppercase leading-tight tracking-wider text-gray-500 dark:text-slate-400">
            {t.leaders}
          </div>
          {topThree(board).map((row: TeamBoardRow, index: number) => (
            <div key={row.officer_id} className="flex items-baseline">
              <span className={`text-xl font-bold ${
                ['text-amber-600 dark:text-amber-400',
                 'text-gray-500 dark:text-slate-300',
                 'text-orange-600 dark:text-orange-400'][index]
              }`}>
                {index + 1}
              </span>
              <span data-testid="leader-name" className="ml-3 text-xl font-semibold">
                {row.officer_name}
              </span>
              <span className="ml-auto text-3xl font-bold tabular-nums">
                {formatLak(row.collected_today)}
              </span>
            </div>
          ))}
        </div>
      )}

      <UninstrumentedBanner sources={board.sources} variant="tv" />

      <TeamBoardTable board={shown} variant="tv" />

      <footer className="mt-auto flex justify-between border-t-2 border-gray-300 pt-2
                         text-sm text-gray-500
                         dark:border-slate-800 dark:text-slate-500">
        <span>
          {t.summary(board.grand_total.officer_count, board.teams.length)}
          {pages.length > 1 && ` · ${t.page(page + 1, pages.length)}`}
        </span>
        <span>
          {t.autoRefresh}{pages.length > 1 && ` · ${t.rotates}`}
        </span>
      </footer>
    </div>
  );
}
